import { useState, useEffect, useCallback } from 'react';
import {
  AlertTriangle, RefreshCw, Search, ChevronLeft, ChevronRight,
  AlertCircle, CheckCircle, RotateCcw, X,
} from 'lucide-react';

import { returnedChequeApi } from '@/api/returnedCheque.api';
import { chequeApi }         from '@/api/cheque.api';
import { usePermission }     from '@/hooks/usePermission';
import { Permissions }       from '@/constants/permissions';
import { Spinner }           from '@/components/ui/Spinner';
import { cn }                from '@/utils/cn';

import type {
  ReturnedChequeListDto,
  ReturnedChequeFilterDto,
  ReturnChequeRequest,
  ReturnChequeResponse,
} from '@/types/returnedCheque.types';
import type { ChequeRecordListDto } from '@/types/cheque.types';
import { GlAccountLookup } from '@/components/lookups/EntityLookups';

// ── Tab type ──────────────────────────────────────────────────────────────────
type Tab = 'list' | 'process';

// ── Return form state ─────────────────────────────────────────────────────────
interface ReturnFormState {
  chequeId:              string;
  chequeNumber:          string;
  returnDate:            string;
  returnReason:          string;
  bankReturnCode:        string;
  returnCharges:         string;
  returnChargeGlAccountId: string;
  remarks:               string;
  /** Whether this cheque may be banked again (ML-11). */
  returnClassification:  'REPRESENTABLE' | 'NON_REPRESENTABLE';
}

const defaultForm = (): ReturnFormState => ({
  chequeId:              '',
  chequeNumber:          '',
  returnDate:            new Date().toISOString().split('T')[0],
  returnReason:          '',
  bankReturnCode:        '',
  returnCharges:         '',
  returnChargeGlAccountId: '',
  remarks:               '',
  // The forgiving default, and the one the server assumes: most returns are a timing problem and
  // the cheque goes back in. Writing off the receipt is the deliberate choice.
  returnClassification:  'REPRESENTABLE',
});

// ── Currency formatter ────────────────────────────────────────────────────────
const formatAmount = (amount: number, currency: string) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);

const formatDate = (d?: string | null) => d ? new Date(d).toLocaleDateString() : '—';

// ═══════════════════════════════════════════════════════════════════════════════
// Page component
// ═══════════════════════════════════════════════════════════════════════════════

export function ReturnedChequePage() {
  const { hasPermission } = usePermission();
  const canReturn = hasPermission(Permissions.BNK_CHEQUE_RETURN);

  // Re-presentation rides the same permission as return — it is a return's other half (GAP-018).
  const [representFor, setRepresentFor] = useState<import('@/types/returnedCheque.types').ReturnedChequeListDto | null>(null);

  // ── Tab state ─────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<Tab>('list');

  // ── List state ────────────────────────────────────────────────────────────
  const [items,        setItems]      = useState<ReturnedChequeListDto[]>([]);
  const [loading,      setLoading]    = useState(false);
  const [error,        setError]      = useState<string | null>(null);
  const [page,         setPage]       = useState(1);
  const [totalCount,   setTotalCount] = useState(0);
  const pageSize = 20;

  const [filter, setFilter] = useState<ReturnedChequeFilterDto>({});
  const [searchInput, setSearchInput] = useState('');

  // ── Process-return state ──────────────────────────────────────────────────
  const [candidateCheques, setCandidateCheques] = useState<ChequeRecordListDto[]>([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [candidateSearch,  setCandidateSearch]  = useState('');

  const [form,          setForm]        = useState<ReturnFormState>(defaultForm());
  const [submitting,    setSubmitting]  = useState(false);
  const [submitError,   setSubmitError] = useState<string | null>(null);
  const [successResult, setSuccessResult] = useState<ReturnChequeResponse | null>(null);

  // ── Load returned cheques list ─────────────────────────────────────────────
  const loadList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await returnedChequeApi.getReturnedCheques({
        ...filter,
        page,
        pageSize,
      });
      if (res.data?.data) {
        setItems(res.data.data.items ?? []);
        setTotalCount(res.data.data.totalCount ?? 0);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load returned cheques';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [filter, page]);

  useEffect(() => { void loadList(); }, [loadList]);

  // ── Load candidate cheques for processing returns ──────────────────────────
  const loadCandidates = useCallback(async () => {
    setCandidateLoading(true);
    try {
      const res = await chequeApi.getList({
        status:         'Deposited',
        search:         candidateSearch || undefined,
        page:           1,
        pageSize:       50,
      });
      if (res.data?.data) {
        setCandidateCheques(res.data.data.items ?? []);
      }
    } catch {
      // silently ignore
    } finally {
      setCandidateLoading(false);
    }
  }, [candidateSearch]);

  useEffect(() => {
    if (activeTab === 'process') loadCandidates();
  }, [activeTab, candidateSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Search / filter handlers ──────────────────────────────────────────────
  const handleSearch = () => {
    setFilter(f => ({ ...f, search: searchInput || undefined }));
    setPage(1);
  };

  const handleDateFilter = (field: 'dateFrom' | 'dateTo', value: string) => {
    setFilter(f => ({ ...f, [field]: value || undefined }));
    setPage(1);
  };

  // ── Select a cheque to return ─────────────────────────────────────────────
  const selectChequeToReturn = (c: ChequeRecordListDto) => {
    setForm(f => ({
      ...f,
      chequeId:     c.id,
      chequeNumber: c.chequeNumber,
    }));
    setSuccessResult(null);
    setSubmitError(null);
  };

  // ── Submit return ─────────────────────────────────────────────────────────
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.chequeId)     { setSubmitError('Please select a cheque to return.'); return; }
    if (!form.returnDate)   { setSubmitError('Return date is required.'); return; }
    if (!form.returnReason) { setSubmitError('Return reason is required.'); return; }

    setSubmitting(true);
    setSubmitError(null);

    try {
      const req: ReturnChequeRequest = {
        returnDate:              form.returnDate,
        returnReason:            form.returnReason,
        bankReturnCode:          form.bankReturnCode    || null,
        returnCharges:           form.returnCharges     ? parseFloat(form.returnCharges) : null,
        returnChargeGlAccountId: form.returnChargeGlAccountId || null,
        remarks:                 form.remarks           || null,
        returnClassification:    form.returnClassification,
      };

      const res = await returnedChequeApi.returnCheque(form.chequeId, req);
      if (res.data?.data) {
        setSuccessResult(res.data.data);
        setForm(defaultForm());
        // Refresh the list
        setPage(1);
        await loadList();
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to process cheque return';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-4 p-4 md:p-6">
      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30">
          <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Returned Cheques
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            View dishonoured cheques and process new returns
          </p>
        </div>
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────── */}
      <div className="flex border-b border-gray-200 dark:border-gray-700">
        {(['list', 'process'] as Tab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={cn(
              'px-4 py-2 text-sm font-medium border-b-2 transition-colors',
              activeTab === tab
                ? 'border-red-500 text-red-600 dark:text-red-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
              tab === 'process' && !canReturn && 'opacity-50 cursor-not-allowed',
            )}
            disabled={tab === 'process' && !canReturn}
          >
            {tab === 'list' ? 'Returned List' : 'Process Return'}
          </button>
        ))}
      </div>

      {/* ══════════════════════════════════════════════════════════════════════
          Tab 1 — Returned List
       ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'list' && (
        <div className="flex flex-col gap-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex gap-2 items-center">
              <input
                type="text"
                placeholder="Search cheque#, customer, reason..."
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
                className="px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 w-64"
              />
              <button
                onClick={handleSearch}
                className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
              >
                <Search size={16} />
              </button>
            </div>
            <div className="flex gap-2 items-center text-sm text-gray-600 dark:text-gray-400">
              <label>From:</label>
              <input
                type="date"
                onChange={e => handleDateFilter('dateFrom', e.target.value)}
                className="px-2 py-1.5 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
              />
              <label>To:</label>
              <input
                type="date"
                onChange={e => handleDateFilter('dateTo', e.target.value)}
                className="px-2 py-1.5 border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
              />
            </div>
            <button
              onClick={() => void loadList()}
              className="p-1.5 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600"
              title="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 text-sm">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {/* Table */}
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                <tr>
                  {['Cheque #', 'Receipt / Voucher', 'Customer / Drawer', 'Bank Account', 'Amount', 'Deposit Date',
                    'Return Date', 'Return Code', 'Reason', 'GL Reversal', 'Classification', ''].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {loading ? (
                  <tr><td colSpan={12} className="py-8 text-center"><Spinner /></td></tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="py-8 text-center text-gray-400 dark:text-gray-500">
                      No returned cheques found.
                    </td>
                  </tr>
                ) : items.map(item => (
                  <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-3 py-2 font-mono font-medium text-gray-900 dark:text-gray-100">
                      {item.chequeNumber}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-gray-500 dark:text-gray-400">
                      {item.referenceDocument ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                      {item.customerName ?? item.supplierName ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      <div>{item.bankAccountCode ?? '—'}</div>
                      <div className="text-xs text-gray-400">{item.bankAccountName ?? 'Not yet deposited'}</div>
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100">
                      {formatAmount(item.amount, item.currencyCode)}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400">
                      {formatDate(item.depositDate)}
                    </td>
                    <td className="px-3 py-2 text-red-600 dark:text-red-400 font-medium">
                      {formatDate(item.returnDate)}
                    </td>
                    <td className="px-3 py-2">
                      {item.bankReturnCode ? (
                        <span className="px-2 py-0.5 rounded bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300 text-xs font-mono">
                          {item.bankReturnCode}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-gray-600 dark:text-gray-400 max-w-[200px] truncate">
                      {item.returnReason ?? '—'}
                    </td>
                    <td className="px-3 py-2">
                      {item.returnGlJournalId || item.receiptReversed ? (
                        <span className="inline-flex items-center gap-1 text-green-600 dark:text-green-400 text-xs">
                          <CheckCircle size={12} /> Posted
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">None</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {!item.isReturned ? (
                        <span className="px-2 py-0.5 rounded bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 text-xs">
                          Re-presented ×{item.representCount}
                        </span>
                      ) : item.returnClassification === 'REPRESENTABLE' ? (
                        <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 text-xs">
                          Representable{item.representCount > 0 ? ` · ×${item.representCount}` : ''}
                        </span>
                      ) : item.returnClassification === 'NON_REPRESENTABLE' ? (
                        <span className="px-2 py-0.5 rounded bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 text-xs">
                          Non-representable
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      {/* Offered only when the cheque may actually come back — a representable one
                          still sitting Returned. The server refuses the rest, but not showing the
                          button keeps the operator from a dead end. */}
                      {/* A re-presented cheque stays on the list as history (its last bounce and
                          count) but is no longer Returned, so it cannot be re-presented again until
                          it bounces again. */}
                      {canReturn && item.isReturned && item.returnClassification === 'REPRESENTABLE' && (
                        <button
                          onClick={() => setRepresentFor(item)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium
                                     bg-orange-50 text-orange-700 hover:bg-orange-100
                                     dark:bg-orange-900/20 dark:text-orange-300"
                        >
                          <RotateCcw size={12} /> Re-present
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>{totalCount} returned cheques</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1 rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronLeft size={16} />
                </button>
                <span>Page {page} of {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="p-1 rounded disabled:opacity-40 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════════════════
          Tab 2 — Process Return
       ══════════════════════════════════════════════════════════════════════ */}
      {activeTab === 'process' && canReturn && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Cheque picker */}
          <div className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              1. Select Cheque to Return
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Shows cheques in Deposited or Presented status that can be returned.
            </p>

            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Search by cheque# or customer..."
                value={candidateSearch}
                onChange={e => setCandidateSearch(e.target.value)}
                className="flex-1 px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
              />
            </div>

            <div className="border rounded-lg divide-y divide-gray-100 dark:divide-gray-700 max-h-96 overflow-y-auto dark:border-gray-700">
              {candidateLoading ? (
                <div className="py-6 text-center"><Spinner /></div>
              ) : candidateCheques.length === 0 ? (
                <div className="py-6 text-center text-sm text-gray-400">
                  No deposited cheques found.
                </div>
              ) : candidateCheques.map(c => (
                <button
                  key={c.id}
                  onClick={() => selectChequeToReturn(c)}
                  className={cn(
                    'w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors',
                    form.chequeId === c.id && 'bg-red-50 dark:bg-red-900/20',
                  )}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <span className="font-mono font-medium text-sm text-gray-900 dark:text-gray-100">
                        {c.chequeNumber}
                      </span>
                      <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                        {c.customerName ?? c.supplierName ?? 'Unknown'}
                      </span>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {formatAmount(c.amount, c.currencyCode)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5">{c.bankAccountName ?? 'Not yet deposited'}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Right: Return form */}
          <div className="flex flex-col gap-4">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              2. Return Details
            </h2>

            {/* Success message */}
            {successResult && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <CheckCircle size={18} className="text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-green-800 dark:text-green-300">
                    Cheque {successResult.chequeNumber} successfully returned.
                  </p>
                  {successResult.returnGlJournalId && (
                    <p className="text-green-700 dark:text-green-400 mt-0.5">
                      Reversal GL journal created: <span className="font-mono">{successResult.returnGlJournalId}</span>
                    </p>
                  )}
                  {successResult.sourceModule === 'AR' && (
                    <p className="text-green-700 dark:text-green-400 mt-0.5">
                      Receipt {successResult.referenceDocument ?? ''} reversed — the invoices it settled are outstanding again.
                    </p>
                  )}
                  {!successResult.returnGlJournalId && successResult.sourceModule !== 'AR' && (
                    <p className="text-green-700 dark:text-green-400 mt-0.5">
                      No GL reversal (cheque had no prior GL posting).
                    </p>
                  )}
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Selected cheque banner */}
              {form.chequeId && (
                <div className="px-3 py-2 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 text-sm">
                  <span className="text-orange-700 dark:text-orange-300 font-medium">
                    Returning cheque: <span className="font-mono">{form.chequeNumber}</span>
                  </span>
                </div>
              )}

              {/* Return Date */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Return Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  required
                  value={form.returnDate}
                  onChange={e => setForm(f => ({ ...f, returnDate: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </div>

              {/* Return Reason */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Return Reason <span className="text-red-500">*</span>
                </label>
                <textarea
                  required
                  rows={2}
                  placeholder="e.g., Insufficient funds, Account closed..."
                  value={form.returnReason}
                  onChange={e => setForm(f => ({ ...f, returnReason: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 resize-none"
                />
              </div>

              {/* Can it be banked again? (ML-11) */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Can this cheque be re-presented?
                </label>
                <select
                  value={form.returnClassification}
                  aria-label="Can this cheque be re-presented?"
                  onChange={e => setForm(f => ({
                    ...f,
                    returnClassification:
                      e.target.value as ReturnFormState['returnClassification'],
                  }))}
                  className="px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                >
                  <option value="REPRESENTABLE">
                    Yes — bank it again (returns to Cheques in Hand)
                  </option>
                  <option value="NON_REPRESENTABLE">
                    No — reverse the receipt (the customer owes it again)
                  </option>
                </select>
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  {form.returnClassification === 'REPRESENTABLE'
                    ? 'The deposit is reversed and the cheque can be deposited again.'
                    : 'The receipt is reversed too, and the invoice it settled goes back to '
                    + 'outstanding on the customer’s account.'}
                </p>
              </div>

              {/* Bank Return Code */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Bank Return Code
                  <span className="ml-1 text-gray-400 font-normal">(optional — e.g., AC 01, RF)</span>
                </label>
                <input
                  type="text"
                  placeholder="AC 01"
                  value={form.bankReturnCode}
                  onChange={e => setForm(f => ({ ...f, bankReturnCode: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </div>

              {/* Return Charges */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Return Charges
                  <span className="ml-1 text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={form.returnCharges}
                  onChange={e => setForm(f => ({ ...f, returnCharges: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                />
              </div>

              {/* Return Charge GL Account */}
              {form.returnCharges && parseFloat(form.returnCharges) > 0 && (
                <div className="flex flex-col gap-1">
                  <GlAccountLookup
                    label="Return Charge GL Account"
                    value={form.returnChargeGlAccountId}
                    onChange={v => setForm(f => ({ ...f, returnChargeGlAccountId: v ?? '' }))}
                    hint="Where the bank's return charge is expensed."
                  />
                </div>
              )}

              {/* Remarks */}
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                  Remarks <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <textarea
                  rows={2}
                  placeholder="Additional notes..."
                  value={form.remarks}
                  onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))}
                  className="px-3 py-1.5 text-sm border rounded-md dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 resize-none"
                />
              </div>

              {/* Submit error */}
              {submitError && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                  <AlertCircle size={14} /> {submitError}
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting || !form.chequeId}
                className={cn(
                  'flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                  'bg-red-600 hover:bg-red-700 text-white',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                {submitting ? (
                  <><Spinner size="sm" /> Processing...</>
                ) : (
                  <><AlertTriangle size={14} /> Process Return</>
                )}
              </button>
            </form>
          </div>
        </div>
      )}

      {activeTab === 'process' && !canReturn && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-300 text-sm">
          <AlertCircle size={16} />
          You do not have permission to process cheque returns.
        </div>
      )}

      {representFor && (
        <RepresentDialog
          item={representFor}
          onCancel={() => setRepresentFor(null)}
          onDone={() => { setRepresentFor(null); loadList(); }}
        />
      )}
    </div>
  );
}

// ── Re-present a representable returned cheque (GAP-018) ──────────────────────

function RepresentDialog({
  item, onCancel, onDone,
}: {
  item: import('@/types/returnedCheque.types').ReturnedChequeListDto;
  onCancel: () => void;
  onDone: () => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [date,   setDate]   = useState(today);
  const [remarks,setRemarks]= useState('');
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]  = useState<string | null>(null);

  const submit = async () => {
    setBusy(true); setError(null);
    try {
      await chequeApi.represent(item.id, date, remarks || undefined);
      onDone();
    } catch (e: unknown) {
      const res = (e as { response?: { data?: { errors?: Array<{ message?: string }>; message?: string } } }).response;
      setError(res?.data?.errors?.[0]?.message ?? res?.data?.message ?? 'Could not re-present the cheque.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onCancel}>
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 p-5 space-y-3 shadow-xl"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100">
            Re-present cheque {item.chequeNumber}
          </h3>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600" aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          A fresh receipt is posted for the cheque and the invoices it had settled are settled again.
          The cheque is back in hand — deposit it again from Bank Deposits. If it bounces again it
          can be returned and re-presented again.
        </p>
        <label className="block text-sm">
          <span className="text-gray-600 dark:text-gray-300">Re-presentation date</span>
          <input type="date" value={date} onChange={e => setDate(e.target.value)}
                 className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 px-2 py-1 text-sm" />
        </label>
        <label className="block text-sm">
          <span className="text-gray-600 dark:text-gray-300">Remarks (optional)</span>
          <input value={remarks} onChange={e => setRemarks(e.target.value)}
                 placeholder="Customer confirms funds are now available"
                 className="mt-1 w-full rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 px-2 py-1 text-sm" />
        </label>
        {error && (
          <div className="flex items-start gap-2 rounded bg-red-50 dark:bg-red-900/20 p-3 text-sm text-red-700 dark:text-red-400">
            <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onCancel} disabled={busy}
                  className="px-3 py-1.5 rounded text-sm border border-gray-300 dark:border-gray-600 disabled:opacity-50">
            Cancel
          </button>
          <button onClick={submit} disabled={busy || !date}
                  className="px-3 py-1.5 rounded text-sm bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50">
            {busy ? 'Re-presenting…' : 'Re-present'}
          </button>
        </div>
      </div>
    </div>
  );
}
